import React, { useState, useEffect } from 'react';
import { DictionaryType, DictionaryEntry } from '../../types';
import { X, Plus, Trash2, Save } from 'lucide-react';

interface EntryModalProps {
  dictType: DictionaryType;
  initialEntry?: DictionaryEntry | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (entry: DictionaryEntry) => Promise<void>;
}

export const EntryModal: React.FC<EntryModalProps> = ({
  dictType,
  initialEntry,
  isOpen,
  onClose,
  onSave,
}) => {
  const [key, setKey] = useState<string>('');
  const [meanings, setMeanings] = useState<string[]>(['']);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (initialEntry) {
      setKey(initialEntry.key);
      setMeanings(initialEntry.meanings.length > 0 ? [...initialEntry.meanings] : ['']);
    } else {
      setKey('');
      setMeanings(['']);
    }
    setError('');
  }, [initialEntry, isOpen]);

  if (!isOpen) return null;

  const handleAddMeaning = () => {
    setMeanings([...meanings, '']);
  };

  const handleRemoveMeaning = (index: number) => {
    if (meanings.length <= 1) {
      setMeanings(['']);
    } else {
      setMeanings(meanings.filter((_, i) => i !== index));
    }
  };

  const handleMeaningChange = (index: number, val: string) => {
    const updated = [...meanings];
    updated[index] = val;
    setMeanings(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!key.trim()) {
      setError('Từ gốc không được để trống.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      // Clean meanings
      const cleanedMeanings = meanings.map((m) => m.trim()).filter(Boolean);
      await onSave({
        type: dictType,
        key: key.trim(),
        meanings: cleanedMeanings,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Lưu từ thất bại.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: '1rem',
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '0.75rem',
          maxWidth: '480px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1rem 1.25rem',
            borderBottom: '1px solid var(--border-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3 style={{ fontSize: '1.125rem', fontWeight: 700, margin: 0 }}>
            {initialEntry ? `Sửa từ (${dictType})` : `Thêm từ (${dictType})`}
          </h3>
          <button onClick={onClose} style={{ color: 'var(--text-muted)' }}>
            <X size={20} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} style={{ overflowY: 'auto', padding: '1.25rem' }}>
          {error && (
            <div
              style={{
                backgroundColor: '#fef2f2',
                color: 'var(--action-danger)',
                padding: '0.625rem 0.875rem',
                borderRadius: '0.375rem',
                fontSize: '0.8125rem',
                marginBottom: '1rem',
                border: '1px solid rgba(228, 59, 18, 0.2)',
              }}
            >
              {error}
            </div>
          )}

          {/* Key Input */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--text-main)',
                marginBottom: '0.375rem',
              }}
            >
              Từ gốc <span style={{ color: 'var(--action-danger)' }}>*</span>
            </label>
            <input
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              disabled={!!initialEntry}
              placeholder="VD: 天道"
              style={{
                width: '100%',
                padding: '0.625rem 0.75rem',
                borderRadius: '0.375rem',
                border: '1px solid var(--border-light)',
                backgroundColor: initialEntry ? '#f1f5f9' : '#ffffff',
                fontSize: '0.9375rem',
                color: 'var(--text-main)',
              }}
            />
          </div>

          {/* Meanings List */}
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-main)' }}>
                Các nghĩa
              </label>
              <button
                type="button"
                onClick={handleAddMeaning}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.75rem',
                  color: 'var(--action-primary)',
                  fontWeight: 600,
                }}
              >
                <Plus size={14} />
                Thêm nghĩa
              </button>
            </div>

            {meanings.map((meaning, index) => (
              <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input
                  type="text"
                  value={meaning}
                  onChange={(e) => handleMeaningChange(index, e.target.value)}
                  placeholder={`Nghĩa ${index + 1}`}
                  style={{
                    flex: 1,
                    padding: '0.5rem 0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid var(--border-light)',
                    fontSize: '0.875rem',
                  }}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveMeaning(index)}
                  style={{
                    color: 'var(--action-danger)',
                    padding: '0.375rem',
                    borderRadius: '0.375rem',
                  }}
                  title="Xóa nghĩa này"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          {/* Footer Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', paddingTop: '0.5rem' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.375rem',
                border: '1px solid var(--border-light)',
                backgroundColor: '#ffffff',
                color: 'var(--text-main)',
                fontSize: '0.875rem',
              }}
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                fontSize: '0.875rem',
                opacity: isSubmitting ? 0.7 : 1,
              }}
            >
              <Save size={16} />
              <span>{isSubmitting ? 'Đang lưu...' : 'Lưu từ'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
